import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type SupportedLanguage = "en" | "zh" | "ja";

type TranslationRecord = Record<string, string>;

const STORAGE_KEY = "beemerce_language";

const translations: Record<SupportedLanguage, TranslationRecord> = {
  en: {
    "nav.products": "Products",
    "nav.about": "About",
    "nav.contact": "Contact",
    "common.loading": "Loading...",
    "common.loadingDescription": "Loading description...",
    "common.loadingProducts": "Loading products...",
    "common.errorPrefix": "Error fetching products: {message}",
    "common.errorUnknown": "Unknown error",
    "common.language.en": "English",
    "common.language.zh": "中文",
    "common.language.ja": "日本語",
    "common.language": "Language",
    "footer.copyright": `© BeeMerce (2024 - ${new Date().getFullYear()}) with ♥️`,
    "product.addToCart": "Add to Cart",
    "product.outOfStock": "Out of Stock",
    "product.inStock": "In Stock",
    "product.inStockCount": "In Stock ({count})",
    "product.inStockCards": "In Stock ({count} cards)",
    "media.none": "No media available",
    "product.fetchDetailFailed": "Unable to fetch product details",
    "product.deleteConfirm":
      "Are you sure you want to delete this product? This action cannot be undone.",
    "product.deleteSuccess": "Product deleted successfully",
    "product.deleteFailed": "Failed to delete: {message}",
    "admin.menu.dashboard": "Dashboard",
    "admin.menu.products": "Products",
    "admin.menu.orders": "Orders",
    "admin.menu.customers": "Customers",
    "admin.menu.pages": "Pages",
    "admin.menu.analytics": "Analytics",
    "admin.menu.payments": "Payments",
    "admin.menu.settings": "Settings",
    "admin.logout": "Logout",
    "admin.panel": "Admin Panel",
    "admin.pages.title": "Page Management",
    "admin.pages.subtitle": "Manage About, Contact and other static pages",
    "admin.pages.action.edit": "Edit Content (Markdown)",
    "admin.pages.action.view": "View Page",
    "admin.pages.modal.title": "Edit Page: {title}",
    "admin.pages.modal.subtitle": "Use Markdown to write page content",
    "admin.pages.field.title": "Page Title",
    "admin.pages.field.content": "Page Content (Markdown)",
    "admin.pages.field.active": "Enable this page",
    "admin.pages.save.success": "Page updated successfully",
    "admin.pages.save.error": "Failed to update page",
    "admin.pages.status.active": "Active",
    "admin.pages.status.inactive": "Inactive",
    "admin.dashboard.title": "Command Center",
    "admin.dashboard.subtitle": "Today overview",
    "admin.dashboard.action.products": "Review Products",
    "admin.dashboard.action.orders": "View Orders",
    "admin.dashboard.metric.products": "Active Products",
    "admin.dashboard.metric.products.helper": "Published catalog",
    "admin.dashboard.metric.orders": "Open Orders",
    "admin.dashboard.metric.orders.helper": "Pending fulfillment",
    "admin.dashboard.metric.revenue": "Revenue Pulse",
    "admin.dashboard.metric.revenue.helper": "Last 7 days",
    "admin.dashboard.metric.customers": "Active Customers",
    "admin.dashboard.metric.customers.helper": "Returning buyers",
    "admin.dashboard.section.pulse": "Revenue Pulse",
    "admin.dashboard.section.pulse.helper": "Weekly momentum",
    "admin.dashboard.section.pulse.legendLeft": "Mon",
    "admin.dashboard.section.pulse.legendRight": "Sun",
    "admin.dashboard.section.priority": "Priority Queue",
    "admin.dashboard.section.priority.helper": "Items needing attention",
    "admin.dashboard.priority.payments": "Payment callbacks",
    "admin.dashboard.priority.payments.helper": "Check gateway sync",
    "admin.dashboard.priority.attention": "Needs review",
    "admin.dashboard.priority.stock": "Low inventory items",
    "admin.dashboard.priority.stock.helper": "Restock before weekend",
    "admin.dashboard.priority.low": "Low",
    "admin.dashboard.priority.messages": "Customer messages",
    "admin.dashboard.priority.messages.helper": "Respond within 24h",
    "admin.dashboard.priority.queue": "Queued",
    "admin.dashboard.section.activity": "Latest activity",
    "admin.dashboard.section.activity.helper": "Auto-updated pulse",
    "admin.dashboard.activity.order": "New order received",
    "admin.dashboard.activity.order.helper": "Pending fulfillment",
    "admin.dashboard.activity.now": "Just now",
    "admin.dashboard.activity.product": "Product updated",
    "admin.dashboard.activity.product.helper": "Media refreshed",
    "admin.dashboard.activity.today": "Today",
    "admin.dashboard.activity.customer": "Customer flagged",
    "admin.dashboard.activity.customer.helper": "Needs follow-up",
    "admin.dashboard.activity.review": "Review",
    "admin.products.title": "Products",
    "admin.products.subtitle": "Catalog control",
    "admin.products.action.add": "Add Product",
    "admin.products.table.id": "ID",
    "admin.products.table.media": "Media",
    "admin.products.table.name": "Name",
    "admin.products.table.category": "Category",
    "admin.products.table.price": "Price",
    "admin.products.table.stock": "Stock",
    "admin.products.table.type": "Type",
    "admin.products.table.status": "Status",
    "admin.products.table.actions": "Actions",
    "admin.products.action.edit": "Edit",
    "admin.products.action.cards": "Cards",
    "admin.products.action.delete": "Delete",
    "admin.products.edit.title.add": "Add Product",
    "admin.products.edit.title.edit": "Edit Product",
    "admin.products.edit.subtitle.add": "Create a new product",
    "admin.products.edit.subtitle.edit": "Modify product information",
    "admin.products.edit.tab.basic": "Basic Info",
    "admin.products.edit.tab.pricing": "Price & Stock",
    "admin.products.edit.tab.media": "Media",
    "admin.products.edit.tab.settings": "Settings",
    "admin.products.edit.field.name": "Product Name",
    "admin.products.edit.field.description": "Description",
    "admin.products.edit.field.category": "Category",
    "admin.products.edit.field.price": "Sale Price",
    "admin.products.edit.field.originalPrice": "Original Price",
    "admin.products.edit.field.stock": "Stock Count",
    "admin.products.edit.field.sales": "Sales Count",
    "admin.products.edit.field.minBuy": "Min Purchase",
    "admin.products.edit.field.maxBuy": "Max Purchase",
    "admin.products.edit.field.cardType": "Card Type",
    "admin.products.edit.field.delivery": "Delivery Method",
    "admin.products.edit.field.sort": "Sort Weight",
    "admin.products.edit.field.active": "Enable Product",
    "admin.products.edit.card.text": "Text Card",
    "admin.products.edit.card.file": "File Card",
    "admin.products.edit.card.api": "API Card",
    "admin.products.edit.delivery.auto": "Auto Delivery",
    "admin.products.edit.delivery.manual": "Manual Delivery",
    "admin.products.edit.media.upload": "Upload Media",
    "admin.products.edit.media.uploading": "Uploading...",
    "admin.products.edit.media.primary": "Primary",
    "admin.products.edit.media.setPrimary": "Set Primary",
    "admin.products.edit.stats.title": "Card Inventory",
    "admin.products.edit.stats.total": "Total",
    "admin.products.edit.stats.used": "Used",
    "admin.products.edit.stats.available": "Available",
    "admin.products.edit.save.loading": "Saving...",
    "admin.products.edit.save.success": "Product saved successfully",
    "admin.products.edit.save.error": "Failed to save product",
    "admin.orders.title": "Order Management",
    "admin.orders.subtitle": "Monitor and fulfill orders",
    "admin.orders.filter.status": "Order Status",
    "admin.orders.filter.payment": "Payment Status",
    "admin.orders.filter.start": "Start Date",
    "admin.orders.filter.end": "End Date",
    "admin.orders.filter.search": "Search",
    "admin.orders.filter.placeholder": "Order No, Email, Product, Contact",
    "admin.orders.action.search": "Search",
    "admin.orders.action.reset": "Reset",
    "admin.orders.batch.selected": "Selected {count} orders",
    "admin.orders.batch.complete": "Batch Complete",
    "admin.orders.batch.cancel": "Batch Cancel",
    "admin.orders.batch.deselect": "Deselect",
    "admin.orders.table.no": "Order No",
    "admin.orders.table.product": "Product",
    "admin.orders.table.customer": "Customer",
    "admin.orders.table.amount": "Amount",
    "admin.orders.table.status": "Status",
    "admin.orders.table.payment": "Payment",
    "admin.orders.table.date": "Date",
    "admin.orders.table.actions": "Actions",
    "admin.orders.table.empty": "No order data available",
    "admin.orders.action.detail": "Details",
    "admin.orders.action.cancel": "Cancel",
    "admin.orders.action.complete": "Complete",
    "admin.orders.status.pending": "Pending",
    "admin.orders.status.paid": "Paid",
    "admin.orders.status.completed": "Completed",
    "admin.orders.status.cancelled": "Cancelled",
    "admin.orders.status.refunded": "Refunded",
    "admin.orders.payment.pending": "Pending",
    "admin.orders.payment.success": "Success",
    "admin.orders.payment.failed": "Failed",
    "admin.orders.payment.cancelled": "Cancelled",
    "admin.orders.pagination.prev": "Previous",
    "admin.orders.pagination.next": "Next",
    "admin.orders.pagination.info": "Page {page} / {total_pages}, Total {total}",
    "admin.customers.title": "Customer Management",
    "admin.customers.subtitle": "Customer stats and history",
    "admin.customers.filter.status": "Status",
    "admin.customers.filter.search": "Search",
    "admin.customers.filter.placeholder": "Email, Contact",
    "admin.customers.table.email": "Email",
    "admin.customers.table.orders": "Orders",
    "admin.customers.table.spent": "Spent",
    "admin.customers.table.last": "Last Order",
    "admin.customers.table.status": "Status",
    "admin.customers.table.actions": "Actions",
    "admin.customers.action.detail": "Detail",
    "admin.analytics.title": "Analytics Dashboard",
    "admin.analytics.subtitle": "Sales and performance metrics",
    "admin.analytics.card.revenue": "Total Revenue",
    "admin.analytics.card.orders": "Total Orders",
    "admin.analytics.card.average": "Average Order",
    "admin.analytics.card.conversion": "Conversion Rate",
    "admin.analytics.section.sales": "Sales Trend",
    "admin.analytics.section.products": "Top Products",
    "admin.payments.title": "Payment Gateways",
    "admin.payments.subtitle": "Manage payment methods and configuration",
    "admin.payments.action.add": "Add Gateway",
    "admin.payments.table.name": "Name",
    "admin.payments.table.type": "Type",
    "admin.payments.table.status": "Status",
    "admin.payments.table.sort": "Sort",
    "admin.payments.table.actions": "Actions",
    "admin.settings.title": "System Settings",
    "admin.settings.subtitle": "Configure global site parameters",
    "admin.settings.action.add": "Add Config",
    "admin.settings.action.saveAll": "Save All Changes",
    "admin.settings.profile.title": "Profile Settings",
    "admin.settings.profile.subtitle": "Update your personal information",
    "admin.settings.profile.username": "Username",
    "admin.settings.profile.email": "Login Email",
    "admin.settings.profile.update": "Update Profile",
    "admin.settings.group.general": "General Settings",
    "admin.settings.group.basic": "Basic Config",
    "admin.settings.group.notification": "Notification Settings",
    "admin.settings.group.order": "Order Settings",
    "admin.settings.group.performance": "Performance Settings",
    "admin.settings.group.security": "Security Settings",
    "admin.settings.modal.title.add": "Add Configuration",
    "admin.settings.modal.title.edit": "Edit Configuration",
    "admin.settings.modal.key": "Config Key",
    "admin.settings.modal.value": "Value",
    "admin.settings.modal.desc": "Description",
    "admin.settings.modal.type": "Data Type",
    "admin.settings.modal.group": "Group",
    "admin.settings.modal.sort": "Sort Order",
    "admin.settings.type.string": "String",
    "admin.settings.type.number": "Number",
    "admin.settings.type.boolean": "Boolean",
    "admin.settings.type.json": "JSON",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.all": "All",
    "login.title": "Admin Login",
    "login.subtitle": "Enter your credentials to access the portal",
    "login.userLabel": "User",
    "login.passwordLabel": "Password",
    "login.usernamePlaceholder": "username / email",
    "login.passwordPlaceholder": "Enter your password",
    "login.submit": "Login",
    "login.loggingIn": "Logging in...",
    "login.error.emptyFields": "Please fill in all fields",
    "login.error.invalid": "Invalid credentials",
    "login.error.generic": "An error occurred during login.",
    "auth.required": "Authentication required",
    "protected.loading": "Loading...",
  },
  zh: {
    "nav.products": "产品中心",
    "nav.about": "关于我们",
    "nav.contact": "联系我们",
    "common.loading": "加载中...",
    "common.loadingDescription": "加载描述中...",
    "common.loadingProducts": "正在加载产品...",
    "common.errorPrefix": "获取产品失败: {message}",
    "common.errorUnknown": "未知错误",
    "common.language.en": "English",
    "common.language.zh": "中文",
    "common.language.ja": "日本語",
    "common.language": "语言",
    "footer.copyright": `© BeeMerce (2024 - ${new Date().getFullYear()}) with ♥️`,
    "product.addToCart": "加入购物车",
    "product.outOfStock": "缺货",
    "product.inStock": "有库存",
    "product.inStockCount": "有库存（{count} 件）",
    "product.inStockCards": "有库存（{count} 张卡密）",
    "media.none": "暂无媒体资源",
    "product.fetchDetailFailed": "无法获取产品详情",
    "product.deleteConfirm": "确定要删除这个产品吗？此操作不可撤销。",
    "product.deleteSuccess": "产品删除成功",
    "product.deleteFailed": "删除失败：{message}",
    "admin.menu.dashboard": "仪表盘",
    "admin.menu.products": "产品",
    "admin.menu.orders": "订单",
    "admin.menu.customers": "客户",
    "admin.menu.pages": "页面管理",
    "admin.menu.analytics": "分析",
    "admin.menu.payments": "支付",
    "admin.menu.settings": "设置",
    "admin.logout": "退出登录",
    "admin.panel": "管理后台",
    "admin.pages.title": "二级页面管理",
    "admin.pages.subtitle": "管理关于、联系我们等静态页面",
    "admin.pages.action.edit": "编辑内容 (Markdown)",
    "admin.pages.action.view": "查看页面",
    "admin.pages.modal.title": "编辑页面: {title}",
    "admin.pages.modal.subtitle": "使用 Markdown 编写页面内容",
    "admin.pages.field.title": "页面标题",
    "admin.pages.field.content": "页面内容 (Markdown)",
    "admin.pages.field.active": "启用此页面",
    "admin.pages.save.success": "页面更新成功",
    "admin.pages.save.error": "页面更新失败",
    "admin.pages.status.active": "已启用",
    "admin.pages.status.inactive": "已禁用",
    "admin.dashboard.title": "运营概览",
    "admin.dashboard.subtitle": "今日经营状态",
    "admin.dashboard.action.products": "管理产品",
    "admin.dashboard.action.orders": "查看订单",
    "admin.dashboard.metric.products": "在售产品",
    "admin.dashboard.metric.products.helper": "已发布商品",
    "admin.dashboard.metric.orders": "待处理订单",
    "admin.dashboard.metric.orders.helper": "待履约",
    "admin.dashboard.metric.revenue": "收入趋势",
    "admin.dashboard.metric.revenue.helper": "近7日",
    "admin.dashboard.metric.customers": "活跃客户",
    "admin.dashboard.metric.customers.helper": "复购客户",
    "admin.dashboard.section.pulse": "收入脉搏",
    "admin.dashboard.section.pulse.helper": "周度走势",
    "admin.dashboard.section.pulse.legendLeft": "周一",
    "admin.dashboard.section.pulse.legendRight": "周日",
    "admin.dashboard.section.priority": "待办优先级",
    "admin.dashboard.section.priority.helper": "需要立即处理",
    "admin.dashboard.priority.payments": "支付回调",
    "admin.dashboard.priority.payments.helper": "待同步网关",
    "admin.dashboard.priority.attention": "待核查",
    "admin.dashboard.priority.stock": "库存告警",
    "admin.dashboard.priority.stock.helper": "补货提醒",
    "admin.dashboard.priority.low": "紧急",
    "admin.dashboard.priority.messages": "客户消息",
    "admin.dashboard.priority.messages.helper": "24小时内回复",
    "admin.dashboard.priority.queue": "排队中",
    "admin.dashboard.section.activity": "最新动态",
    "admin.dashboard.section.activity.helper": "自动更新",
    "admin.dashboard.activity.order": "新订单进入",
    "admin.dashboard.activity.order.helper": "待发货提醒",
    "admin.dashboard.activity.now": "刚刚",
    "admin.dashboard.activity.product": "产品更新",
    "admin.dashboard.activity.product.helper": "已添加新媒体",
    "admin.dashboard.activity.today": "今天",
    "admin.dashboard.activity.customer": "客户标记",
    "admin.dashboard.activity.customer.helper": "需要跟进",
    "admin.dashboard.activity.review": "待复核",
    "admin.products.title": "产品管理",
    "admin.products.subtitle": "目录与库存",
    "admin.products.action.add": "新增产品",
    "admin.products.table.id": "ID",
    "admin.products.table.media": "媒体",
    "admin.products.table.name": "名称",
    "admin.products.table.category": "分类",
    "admin.products.table.price": "价格",
    "admin.products.table.stock": "库存",
    "admin.products.table.type": "类型",
    "admin.products.table.status": "状态",
    "admin.products.table.actions": "操作",
    "admin.products.action.edit": "编辑",
    "admin.products.action.cards": "卡密",
    "admin.products.action.delete": "删除",
    "admin.products.edit.title.add": "添加产品",
    "admin.products.edit.title.edit": "编辑产品",
    "admin.products.edit.subtitle.add": "创建新的产品",
    "admin.products.edit.subtitle.edit": "修改产品信息",
    "admin.products.edit.tab.basic": "基本信息",
    "admin.products.edit.tab.pricing": "价格库存",
    "admin.products.edit.tab.media": "媒体管理",
    "admin.products.edit.tab.settings": "产品设置",
    "admin.products.edit.field.name": "产品名称",
    "admin.products.edit.field.description": "产品描述",
    "admin.products.edit.field.category": "产品分类",
    "admin.products.edit.field.price": "售价",
    "admin.products.edit.field.originalPrice": "原价",
    "admin.products.edit.field.stock": "库存数量",
    "admin.products.edit.field.sales": "销售数量",
    "admin.products.edit.field.minBuy": "最小购买数量",
    "admin.products.edit.field.maxBuy": "最大购买数量",
    "admin.products.edit.field.cardType": "卡密类型",
    "admin.products.edit.field.delivery": "发货方式",
    "admin.products.edit.field.sort": "排序权重",
    "admin.products.edit.field.active": "启用产品",
    "admin.products.edit.card.text": "文本卡密",
    "admin.products.edit.card.file": "文件卡密",
    "admin.products.edit.card.api": "API卡密",
    "admin.products.edit.delivery.auto": "自动发货",
    "admin.products.edit.delivery.manual": "手动发货",
    "admin.products.edit.media.upload": "上传媒体文件",
    "admin.products.edit.media.uploading": "正在上传...",
    "admin.products.edit.media.primary": "主图",
    "admin.products.edit.media.setPrimary": "设为主图",
    "admin.products.edit.stats.title": "卡密库存",
    "admin.products.edit.stats.total": "总数",
    "admin.products.edit.stats.used": "已用",
    "admin.products.edit.stats.available": "可用",
    "admin.products.edit.save.loading": "保存中...",
    "admin.products.edit.save.success": "产品保存成功",
    "admin.products.edit.save.error": "保存产品失败",
    "admin.orders.title": "订单管理",
    "admin.orders.subtitle": "监控并处理订单",
    "admin.orders.filter.status": "订单状态",
    "admin.orders.filter.payment": "支付状态",
    "admin.orders.filter.start": "开始日期",
    "admin.orders.filter.end": "结束日期",
    "admin.orders.filter.search": "搜索",
    "admin.orders.filter.placeholder": "订单号、邮箱、产品名称、联系方式",
    "admin.orders.action.search": "搜索",
    "admin.orders.action.reset": "重置",
    "admin.orders.batch.selected": "已选择 {count} 个订单",
    "admin.orders.batch.complete": "批量完成",
    "admin.orders.batch.cancel": "批量取消",
    "admin.orders.batch.deselect": "取消选择",
    "admin.orders.table.no": "订单号",
    "admin.orders.table.product": "产品",
    "admin.orders.table.customer": "客户信息",
    "admin.orders.table.amount": "金额",
    "admin.orders.table.status": "订单状态",
    "admin.orders.table.payment": "支付状态",
    "admin.orders.table.date": "创建时间",
    "admin.orders.table.actions": "操作",
    "admin.orders.table.empty": "暂无订单数据",
    "admin.orders.action.detail": "详情",
    "admin.orders.action.cancel": "取消",
    "admin.orders.action.complete": "完成",
    "admin.orders.status.pending": "待支付",
    "admin.orders.status.paid": "已支付",
    "admin.orders.status.completed": "已完成",
    "admin.orders.status.cancelled": "已取消",
    "admin.orders.status.refunded": "已退款",
    "admin.orders.payment.pending": "待支付",
    "admin.orders.payment.success": "支付成功",
    "admin.orders.payment.failed": "支付失败",
    "admin.orders.payment.cancelled": "支付取消",
    "admin.orders.pagination.prev": "上一页",
    "admin.orders.pagination.next": "下一页",
    "admin.orders.pagination.info": "第 {page} / {total_pages} 页，共 {total} 条",
    "admin.customers.title": "客户管理",
    "admin.customers.subtitle": "客户统计与历史",
    "admin.customers.filter.status": "状态",
    "admin.customers.filter.search": "搜索",
    "admin.customers.filter.placeholder": "邮箱、联系方式",
    "admin.customers.table.email": "邮箱",
    "admin.customers.table.orders": "订单数",
    "admin.customers.table.spent": "消费总额",
    "admin.customers.table.last": "最后订单",
    "admin.customers.table.status": "状态",
    "admin.customers.table.actions": "操作",
    "admin.customers.action.detail": "详情",
    "admin.analytics.title": "分析仪表盘",
    "admin.analytics.subtitle": "销售与性能指标数据",
    "admin.analytics.card.revenue": "总收入",
    "admin.analytics.card.orders": "总订单",
    "admin.analytics.card.average": "客单价",
    "admin.analytics.card.conversion": "转化率",
    "admin.analytics.section.sales": "销售趋势",
    "admin.analytics.section.products": "热销产品",
    "admin.payments.title": "支付网关",
    "admin.payments.subtitle": "管理支付方式与配置",
    "admin.payments.action.add": "新增网关",
    "admin.payments.table.name": "名称",
    "admin.payments.table.type": "类型",
    "admin.payments.table.status": "状态",
    "admin.payments.table.sort": "排序",
    "admin.payments.table.actions": "操作",
    "admin.settings.title": "系统设置",
    "admin.settings.subtitle": "配置全局站点参数",
    "admin.settings.action.add": "新增配置",
    "admin.settings.action.saveAll": "保存所有更改",
    "admin.settings.profile.title": "个人资料设置",
    "admin.settings.profile.subtitle": "更新您的个人信息",
    "admin.settings.profile.username": "用户名",
    "admin.settings.profile.email": "登录邮箱",
    "admin.settings.profile.update": "更新资料",
    "admin.settings.group.general": "通用设置",
    "admin.settings.group.basic": "基础配置",
    "admin.settings.group.notification": "通知设置",
    "admin.settings.group.order": "订单设置",
    "admin.settings.group.performance": "性能设置",
    "admin.settings.group.security": "安全设置",
    "admin.settings.modal.title.add": "新增配置",
    "admin.settings.modal.title.edit": "编辑配置",
    "admin.settings.modal.key": "配置键名",
    "admin.settings.modal.value": "配置值",
    "admin.settings.modal.desc": "说明描述",
    "admin.settings.modal.type": "数据类型",
    "admin.settings.modal.group": "配置分组",
    "admin.settings.modal.sort": "排序权重",
    "admin.settings.type.string": "字符串",
    "admin.settings.type.number": "数字",
    "admin.settings.type.boolean": "布尔值",
    "admin.settings.type.json": "JSON",
    "common.save": "保存",
    "common.cancel": "取消",
    "common.all": "全部",
    "login.title": "管理员登录",
    "login.subtitle": "输入凭据以访问管理后台",
    "login.userLabel": "账号",
    "login.passwordLabel": "密码",
    "login.usernamePlaceholder": "用户名 / 邮箱",
    "login.passwordPlaceholder": "请输入密码",
    "login.submit": "登录",
    "login.loggingIn": "登录中...",
    "login.error.emptyFields": "请填写所有必填项",
    "login.error.invalid": "凭据无效",
    "login.error.generic": "登录时发生错误。",
    "auth.required": "需要身份验证",
    "protected.loading": "加载中...",
  },
  ja: {
    "nav.products": "製品一覧",
    "nav.about": "当店について",
    "nav.contact": "お問い合わせ",
    "common.loading": "読み込み中...",
    "common.loadingDescription": "説明を読み込み中...",
    "common.loadingProducts": "商品を読み込み中...",
    "common.errorPrefix": "商品取得に失敗しました: {message}",
    "common.errorUnknown": "不明なエラー",
    "common.language.en": "English",
    "common.language.zh": "中文",
    "common.language.ja": "日本語",
    "common.language": "言語",
    "footer.copyright": `© BeeMerce (2024 - ${new Date().getFullYear()}) with ♥️`,
    "product.addToCart": "カートに追加",
    "product.outOfStock": "在庫切れ",
    "product.inStock": "在庫あり",
    "product.inStockCount": "在庫あり（{count} 件）",
    "product.inStockCards": "在庫あり（{count} 枚のカード）",
    "media.none": "利用可能なメディアはありません",
    "product.fetchDetailFailed": "商品詳細を取得できませんでした",
    "product.deleteConfirm":
      "この商品を削除しますか？この操作は元に戻せません。",
    "product.deleteSuccess": "商品を削除しました",
    "product.deleteFailed": "削除に失敗しました: {message}",
    "admin.menu.dashboard": "ダッシュボード",
    "admin.menu.products": "製品",
    "admin.menu.orders": "注文",
    "admin.menu.customers": "顧客",
    "admin.menu.pages": "ページ管理",
    "admin.menu.analytics": "分析",
    "admin.menu.payments": "決済",
    "admin.menu.settings": "設定",
    "admin.logout": "ログアウト",
    "admin.panel": "管理パネル",
    "admin.pages.title": "固定ページ管理",
    "admin.pages.subtitle": "概要、お問い合わせなどの静的ページを管理します",
    "admin.pages.action.edit": "内容を編集 (Markdown)",
    "admin.pages.action.view": "ページを表示",
    "admin.pages.modal.title": "ページ編集: {title}",
    "admin.pages.modal.subtitle": "Markdownを使用してページ内容を記述します",
    "admin.pages.field.title": "ページタイトル",
    "admin.pages.field.content": "ページ内容 (Markdown)",
    "admin.pages.field.active": "このページを有効にする",
    "admin.pages.save.success": "ページを更新しました",
    "admin.pages.save.error": "ページの更新に失敗しました",
    "admin.pages.status.active": "有効",
    "admin.pages.status.inactive": "無効",
    "admin.dashboard.title": "運用ダッシュボード",
    "admin.dashboard.subtitle": "本日の概況",
    "admin.dashboard.action.products": "商品管理",
    "admin.dashboard.action.orders": "注文を見る",
    "admin.dashboard.metric.products": "販売中の商品",
    "admin.dashboard.metric.products.helper": "公開済みカタログ",
    "admin.dashboard.metric.orders": "未処理注文",
    "admin.dashboard.metric.orders.helper": "発送待ち",
    "admin.dashboard.metric.revenue": "売上の動き",
    "admin.dashboard.metric.revenue.helper": "直近7日",
    "admin.dashboard.metric.customers": "アクティブ顧客",
    "admin.dashboard.metric.customers.helper": "リピート顧客",
    "admin.dashboard.section.pulse": "売上の鼓動",
    "admin.dashboard.section.pulse.helper": "週次トレンド",
    "admin.dashboard.section.pulse.legendLeft": "月",
    "admin.dashboard.section.pulse.legendRight": "日",
    "admin.dashboard.section.priority": "優先キュー",
    "admin.dashboard.section.priority.helper": "至急対応",
    "admin.dashboard.priority.payments": "決済同期",
    "admin.dashboard.priority.payments.helper": "ゲートウェイ確認",
    "admin.dashboard.priority.attention": "要確認",
    "admin.dashboard.priority.stock": "在庫アラート",
    "admin.dashboard.priority.stock.helper": "補充が必要",
    "admin.dashboard.priority.low": "緊急",
    "admin.dashboard.priority.messages": "顧客メッセージ",
    "admin.dashboard.priority.messages.helper": "24時間以内返信",
    "admin.dashboard.priority.queue": "待機中",
    "admin.dashboard.section.activity": "最新アクティビティ",
    "admin.dashboard.section.activity.helper": "自動更新",
    "admin.dashboard.activity.order": "新規注文",
    "admin.dashboard.activity.order.helper": "出荷待ち",
    "admin.dashboard.activity.now": "たった今",
    "admin.dashboard.activity.product": "商品更新",
    "admin.dashboard.activity.product.helper": "新しいメディア追加",
    "admin.dashboard.activity.today": "今日",
    "admin.dashboard.activity.customer": "顧客フラグ",
    "admin.dashboard.activity.customer.helper": "要フォロー",
    "admin.dashboard.activity.review": "要確認",
    "admin.products.title": "商品管理",
    "admin.products.subtitle": "カタログ管理",
    "admin.products.action.add": "商品を追加",
    "admin.products.table.id": "ID",
    "admin.products.table.media": "メディア",
    "admin.products.table.name": "商品名",
    "admin.products.table.category": "カテゴリー",
    "admin.products.table.price": "価格",
    "admin.products.table.stock": "在庫",
    "admin.products.table.type": "タイプ",
    "admin.products.table.status": "ステータス",
    "admin.products.table.actions": "操作",
    "admin.products.action.edit": "編集",
    "admin.products.action.cards": "カード",
    "admin.products.action.delete": "削除",
    "admin.products.edit.title.add": "商品を追加",
    "admin.products.edit.title.edit": "商品を編集",
    "admin.products.edit.subtitle.add": "新しい商品を作成します",
    "admin.products.edit.subtitle.edit": "商品情報を変更します",
    "admin.products.edit.tab.basic": "基本情報",
    "admin.products.edit.tab.pricing": "価格と在庫",
    "admin.products.edit.tab.media": "メディア管理",
    "admin.products.edit.tab.settings": "商品設定",
    "admin.products.edit.field.name": "商品名",
    "admin.products.edit.field.description": "商品説明",
    "admin.products.edit.field.category": "カテゴリー",
    "admin.products.edit.field.price": "販売価格",
    "admin.products.edit.field.originalPrice": "定価",
    "admin.products.edit.field.stock": "在庫数",
    "admin.products.edit.field.sales": "販売数",
    "admin.products.edit.field.minBuy": "最小購入数",
    "admin.products.edit.field.maxBuy": "最大購入数",
    "admin.products.edit.field.cardType": "カードタイプ",
    "admin.products.edit.field.delivery": "発送方法",
    "admin.products.edit.field.sort": "並べ替えの重み",
    "admin.products.edit.field.active": "商品を有効にする",
    "admin.products.edit.card.text": "テキストカード",
    "admin.products.edit.card.file": "ファイルカード",
    "admin.products.edit.card.api": "APIカード",
    "admin.products.edit.delivery.auto": "自動発送",
    "admin.products.edit.delivery.manual": "手動発送",
    "admin.products.edit.media.upload": "メディアをアップロード",
    "admin.products.edit.media.uploading": "アップロード中...",
    "admin.products.edit.media.primary": "メイン",
    "admin.products.edit.media.setPrimary": "メインに設定",
    "admin.products.edit.stats.title": "カード在庫",
    "admin.products.edit.stats.total": "合計",
    "admin.products.edit.stats.used": "使用済み",
    "admin.products.edit.stats.available": "利用可能",
    "admin.products.edit.save.loading": "保存中...",
    "admin.products.edit.save.success": "商品を保存しました",
    "admin.products.edit.save.error": "商品の保存に失敗しました",
    "admin.orders.title": "注文管理",
    "admin.orders.subtitle": "注文の監視と履行",
    "admin.orders.filter.status": "注文ステータス",
    "admin.orders.filter.payment": "決済ステータス",
    "admin.orders.filter.start": "開始日",
    "admin.orders.filter.end": "終了日",
    "admin.orders.filter.search": "検索",
    "admin.orders.filter.placeholder": "注文番号、メール、商品、連絡先",
    "admin.orders.action.search": "検索",
    "admin.orders.action.reset": "リセット",
    "admin.orders.batch.selected": "{count} 件の注文を選択中",
    "admin.orders.batch.complete": "一括完了",
    "admin.orders.batch.cancel": "一括キャンセル",
    "admin.orders.batch.deselect": "選択解除",
    "admin.orders.table.no": "注文番号",
    "admin.orders.table.product": "商品",
    "admin.orders.table.customer": "顧客情報",
    "admin.orders.table.amount": "金額",
    "admin.orders.table.status": "注文ステータス",
    "admin.orders.table.payment": "決済ステータス",
    "admin.orders.table.date": "作成日時",
    "admin.orders.table.actions": "操作",
    "admin.orders.table.empty": "注文データはありません",
    "admin.orders.action.detail": "詳細",
    "admin.orders.action.cancel": "キャンセル",
    "admin.orders.action.complete": "完了",
    "admin.orders.status.pending": "保留中",
    "admin.orders.status.paid": "支払い済み",
    "admin.orders.status.completed": "完了",
    "admin.orders.status.cancelled": "キャンセル済み",
    "admin.orders.status.refunded": "返金済み",
    "admin.orders.payment.pending": "保留中",
    "admin.orders.payment.success": "成功",
    "admin.orders.payment.failed": "失敗",
    "admin.orders.payment.cancelled": "キャンセル済み",
    "admin.orders.pagination.prev": "前へ",
    "admin.orders.pagination.next": "次へ",
    "admin.orders.pagination.info": "{page} / {total_pages} ページ、合计 {total} 件",
    "admin.customers.title": "顧客管理",
    "admin.customers.subtitle": "顧客統計と履歴",
    "admin.customers.filter.status": "ステータス",
    "admin.customers.filter.search": "検索",
    "admin.customers.filter.placeholder": "メール、連絡先",
    "admin.customers.table.email": "メール",
    "admin.customers.table.orders": "注文数",
    "admin.customers.table.spent": "支出総額",
    "admin.customers.table.last": "最終注文",
    "admin.customers.table.status": "ステータス",
    "admin.customers.table.actions": "操作",
    "admin.customers.action.detail": "詳細",
    "admin.analytics.title": "分析ダッシュボード",
    "admin.analytics.subtitle": "売上とパフォーマンス指標",
    "admin.analytics.card.revenue": "総売上",
    "admin.analytics.card.orders": "総注文数",
    "admin.analytics.card.average": "客単価",
    "admin.analytics.card.conversion": "CVR",
    "admin.analytics.section.sales": "売上トレンド",
    "admin.analytics.section.products": "人気商品",
    "admin.payments.title": "決済ゲートウェイ",
    "admin.payments.subtitle": "決済方法と設定の管理",
    "admin.payments.action.add": "ゲートウェイ追加",
    "admin.payments.table.name": "名前",
    "admin.payments.table.type": "タイプ",
    "admin.payments.table.status": "ステータス",
    "admin.payments.table.sort": "並べ替え",
    "admin.payments.table.actions": "操作",
    "admin.settings.title": "システム設定",
    "admin.settings.subtitle": "グローバルサイトパラメータの設定",
    "admin.settings.action.add": "新規設定",
    "admin.settings.action.saveAll": "すべての変更を保存",
    "admin.settings.profile.title": "プロフィール設定",
    "admin.settings.profile.subtitle": "個人情報を更新します",
    "admin.settings.profile.username": "ユーザー名",
    "admin.settings.profile.email": "ログインメールアドレス",
    "admin.settings.profile.update": "プロフィールを更新",
    "admin.settings.group.general": "全般設定",
    "admin.settings.group.basic": "基本設定",
    "admin.settings.group.notification": "通知設定",
    "admin.settings.group.order": "注文設定",
    "admin.settings.group.performance": "パフォーマンス設定",
    "admin.settings.group.security": "セキュリティ設定",
    "admin.settings.modal.title.add": "設定を追加",
    "admin.settings.modal.title.edit": "設定を編集",
    "admin.settings.modal.key": "設定キー",
    "admin.settings.modal.value": "設定値",
    "admin.settings.modal.desc": "説明",
    "admin.settings.modal.type": "データ型",
    "admin.settings.modal.group": "グループ",
    "admin.settings.modal.sort": "並べ替え順",
    "admin.settings.type.string": "文字列",
    "admin.settings.type.number": "数値",
    "admin.settings.type.boolean": "真偽值",
    "admin.settings.type.json": "JSON",
    "common.save": "保存",
    "common.cancel": "キャンセル",
    "common.all": "すべて",
    "login.title": "管理者ログイン",
    "login.subtitle": "ポータルにアクセスするための資格情報を入力してください",
    "login.userLabel": "ユーザー",
    "login.passwordLabel": "パスワード",
    "login.usernamePlaceholder": "ユーザー名 / メール",
    "login.passwordPlaceholder": "パスワードを入力してください",
    "login.submit": "ログイン",
    "login.loggingIn": "ログイン中...",
    "login.error.emptyFields": "すべての必須項目を入力してください",
    "login.error.invalid": "認証情報が正しくありません",
    "login.error.generic": "ログイン中にエラーが発生しました。",
    "auth.required": "認証が必要です",
    "protected.loading": "読み込み中...",
  },
};

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

const getInitialLanguage = (): SupportedLanguage => {
  if (typeof window === "undefined") {
    return "en";
  }
  const stored = window.localStorage.getItem(
    STORAGE_KEY,
  ) as SupportedLanguage | null;
  if (stored && ["en", "zh", "ja"].includes(stored)) {
    return stored;
  }
  const browserLang = window.navigator.language.slice(0, 2);
  if (browserLang === "zh") return "zh";
  if (browserLang === "ja") return "ja";
  return "en";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] =
    useState<SupportedLanguage>(getInitialLanguage);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, language);
    }
  }, [language]);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const dict = translations[language] || translations.en;
      const fallbackDict = translations.en;
      const template = dict[key] ?? fallbackDict[key] ?? key;
      if (!params) return template;

      return template.replace(/\{(\w+)\}/g, (match, paramKey) => {
        if (Object.prototype.hasOwnProperty.call(params, paramKey)) {
          return String(params[paramKey]);
        }
        return match;
      });
    },
    [language],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const availableLanguages: {
  value: SupportedLanguage;
  labelKey: string;
}[] = [
  { value: "zh", labelKey: "common.language.zh" },
  { value: "en", labelKey: "common.language.en" },
  { value: "ja", labelKey: "common.language.ja" },
];
